/* eslint-disable react/display-name, no-use-before-define */
import { Fragment, Suspense, createElement, isValidElement, memo, useId } from "react"
import type { ComponentType, FunctionComponent, ReactElement, ReactNode } from "react"

import { propsAreEqual } from "@/3514/utils"

import { ContentLoader } from "./loaders"

type TProps<CProps> = {
    component: ReactElement | ComponentType
    componentProps?: CProps
    isLoading?: boolean
    isReady?: boolean
    withoutSuspense?: boolean
    withEnforcedSuspense?: boolean
    loadingFallback?: ReactElement
}

/**
 * Component-helper for visually dynamic component loading. Preferred to use with React.lazy()
 * Renders <ContentLoader /> component for loading state be default
 *
 * @param isLoading boolean parameter indicating fetch status process
 * @param component ReactElement to be displayed once data is loaded
 * @param componentProps boolean parameter indicating fetch status process
 * @param isReady optional boolean parameter indicating fetch status completion
 * @param withoutSuspense boolean parameter for disabling internal wrapping in <React.Suspense />
 * @param loadingFallback ReactElement to display instead of component while loading
 * @param noDataFallback ReactElement for the empty data to be displayed instead of component
 * @param withEnforcedSuspense for suspense queries support
 * @return {ReactElement} based on internal condition
 */
const Loadable: <TComponentProps>(props: TProps<TComponentProps>) => ReactElement = memo(
    <TComponentProps,>({
        component: C,
        componentProps,
        isLoading = false,
        isReady = true,
        withoutSuspense = false,
        loadingFallback = undefined,
        withEnforcedSuspense = false
    }: TProps<TComponentProps>): ReactElement => {
        const loaderUniqueKey: string = useId()
        const LoadingFallback: ReactElement = loadingFallback ?? <ContentLoader uniqueKey={loaderUniqueKey} />

        const SuspenseWrapper: FunctionComponent<{ children: ReactNode }> = ({
            children
        }: {
            children: ReactNode
        }): ReactElement => <Suspense fallback={LoadingFallback}>{children}</Suspense>

        const Wrapper: FunctionComponent<{ children: ReactNode }> = ({
            children
        }: {
            children: ReactNode
        }): ReactElement =>
            withoutSuspense ? <Fragment>{children}</Fragment> : <SuspenseWrapper>{children}</SuspenseWrapper>

        const RenderedComponent: ReactElement = isValidElement(C)
            ? C
            : createElement(C as ComponentType, componentProps)

        return withEnforcedSuspense ? (
            <SuspenseWrapper>{RenderedComponent}</SuspenseWrapper>
        ) : (
            <Wrapper>{isLoading ? LoadingFallback : isReady ? RenderedComponent : null}</Wrapper>
        )
    },
    propsAreEqual
) as <TComponentProps>(props: TProps<TComponentProps>) => ReactElement

export { Loadable }
